/* Register the service worker and auto-update the page when a new SW is ready.
 *
 * Flow:
 *  1. Register /service-worker.js.
 *  2. When a new SW is installed AND there's already a controller (= we are
 *     replacing an older SW), tell the new SW to skipWaiting immediately.
 *  3. Listen for `controllerchange` → reload the page exactly once so the user
 *     sees the new build without needing to clear cache or reinstall the PWA.
 *  4. Periodically poll for SW updates (once per hour) so long-lived sessions
 *     pick up new deploys automatically.
 */
/**
 * Path of the main JS bundle actually running in this tab, read from the
 * <script> tag CRA injects into index.html (e.g. "/static/js/main.4f8348c6.js").
 * Returns null in dev (webpack-dev-server serves an unhashed bundle).
 */
function runningMainBundle() {
  try {
    const el = document.querySelector('script[src*="/static/js/main."]');
    if (!el) return null;
    return new URL(el.src, window.location.origin).pathname;
  } catch {
    return null;
  }
}

/**
 * Compare the bundle running in this tab against the freshly deployed
 * asset-manifest.json. If a newer build is live, drop all caches and reload so
 * the user is never stuck on stale code. Safe against reload loops via a
 * per-build sessionStorage guard. Called on load, on focus, and on every login.
 */
export async function checkForNewBuild({ reloadIfStale = true } = {}) {
  try {
    const running = runningMainBundle();
    if (!running) return false; // dev, or can't determine — nothing to do

    // Cache-bust the manifest so neither the SW nor the HTTP cache can serve a stale copy.
    const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return false;
    const manifest = await res.json();
    const deployed = manifest && manifest.files && manifest.files["main.js"];
    if (!deployed) return false;

    const deployedPath = new URL(deployed, window.location.origin).pathname;
    if (deployedPath === running) return false; // already on the latest build

    // New build detected. Instead of silently reloading (which interrupts a user
    // mid-task) or making them hard-refresh by hand, surface a prompt so they
    // refresh when it's convenient. Guarded to notify once per build per tab.
    const guardKey = `emedbay_build_notified_${deployedPath}`;
    if (sessionStorage.getItem(guardKey)) return true;
    sessionStorage.setItem(guardKey, "1");
    // Flag for a listener that mounts AFTER this fires (e.g. on first load).
    try { window.__emedbayUpdatePending = deployedPath; } catch {}
    try {
      window.dispatchEvent(new CustomEvent("emedbay:update-available", { detail: { build: deployedPath } }));
    } catch {}
    return true;
  } catch {
    return false;
  }
}

/** Short id of the build running in this tab, e.g. "b01a8a66". "dev" outside a build. */
export function runningBuildId() {
  const path = runningMainBundle();
  const m = path && path.match(/main\.([a-z0-9]+)\.js/);
  return m ? m[1] : "dev";
}

/** Build id currently deployed on the server, or "" if it can't be determined. */
export async function deployedBuildId() {
  try {
    const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return "";
    const manifest = await res.json();
    const m = String(manifest?.files?.["main.js"] || "").match(/main\.([a-z0-9]+)\.js/);
    return m ? m[1] : "";
  } catch {
    return "";
  }
}

/**
 * Last-resort update for a device stuck on an old build: bin every cache, drop
 * the service worker entirely, and reload past the HTTP cache. The next load
 * re-registers a fresh worker. Heavier than checkForNewBuild() — that one is
 * automatic; this is the button a human presses when a station is misbehaving.
 */
export async function forceUpdateNow() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    // Clear the per-build reload guards so checkForNewBuild() can act again.
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("emedbay_build_reload_"))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {}

  // A changed query string defeats any HTTP cache still holding index.html.
  const url = new URL(window.location.href);
  url.searchParams.set("_u", String(Date.now()));
  window.location.replace(url.toString());
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;

  // The build-freshness check runs even without a service worker (e.g. plain
  // browsers or when SW registration is skipped): on initial load, on tab focus,
  // and when the tab becomes visible again after a deploy.
  checkForNewBuild();
  window.addEventListener("focus", () => checkForNewBuild());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForNewBuild();
  });

  if (!("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production") return;

  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    // Give the new SW a tick to fully take over, then refresh.
    window.location.reload();
  });

  const onReady = async () => {
    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js");

      // A waiting/new worker means a newer build is available. Don't swap it in
      // silently (that reloads mid-task) — surface the update prompt instead and
      // let the user refresh when ready (the Refresh button calls forceUpdateNow).
      if (reg.waiting && navigator.serviceWorker.controller) {
        checkForNewBuild();
      }

      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener("statechange", () => {
          if (next.state === "installed" && navigator.serviceWorker.controller) {
            checkForNewBuild();
          }
        });
      });

      // Periodic update check (every 60 minutes, also on visibility/focus).
      const check = () => reg.update().catch(() => null);
      setInterval(check, 60 * 60 * 1000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
      window.addEventListener("focus", check);
    } catch (_) {
      // ignore registration errors
    }
  };

  if (document.readyState === "complete") onReady();
  else window.addEventListener("load", onReady);
}
